import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { canView } from "../lib/permissions";
import { Button } from "./ui/button";
import {
  LayoutDashboard, ShoppingCart, Receipt, History, FileText, CalendarSearch,
  CalendarRange, CalendarDays, BarChart3, Lock, Package, Users, LogOut, Menu, X, Drumstick, Briefcase,
} from "lucide-react";

const LINKS = [
  { to: "/dashboard",          seccion: "dashboard",         label: "Dashboard",            icon: LayoutDashboard },
  { to: "/captura-ventas",     seccion: "captura_ventas",    label: "Captura Ventas",       icon: ShoppingCart },
  { to: "/captura-gastos",     seccion: "captura_gastos",    label: "Captura Gastos",       icon: Receipt },
  { to: "/historial-ventas",   seccion: "historial_ventas",  label: "Historial Ventas",     icon: History },
  { to: "/historial-gastos",   seccion: "historial_gastos",  label: "Historial Gastos",     icon: History },
  { to: "/consulta-fecha",     seccion: "consulta_fecha",    label: "Consulta por Fecha",   icon: CalendarSearch },
  { to: "/reporte-semanal",    seccion: "reporte_semanal",   label: "Reporte Semanal",      icon: CalendarRange },
  { to: "/reporte-mensual",    seccion: "reporte_mensual",   label: "Reporte Mensual",      icon: CalendarDays },
  { to: "/estado-resultados",  seccion: "estado_resultados", label: "Estado de Resultados", icon: BarChart3 },
  { to: "/cierre-diario",      seccion: "cierre_diario",     label: "Cierre Diario",        icon: Lock },
  { to: "/productos",          seccion: "productos",         label: "Productos / Costos",   icon: Package },
  { to: "/gastos-operativos",  seccion: "gastos_operativos", label: "Gastos Operativos",    icon: Briefcase },
  { to: "/usuarios",           seccion: "_admin_only_",      label: "Usuarios",             icon: Users },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const links = LINKS.filter((l) => {
    if (l.seccion === "_admin_only_") return user?.role === "admin";
    return canView(user, l.seccion);
  });

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Topbar mobile */}
      <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center justify-between px-4 h-14">
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="p-2 -ml-2 rounded hover:bg-slate-100"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <div className="flex items-center gap-2 font-bold tracking-tight">
          <Drumstick className="w-5 h-5 text-blue-600" />
          <span>Cerebro Financiero</span>
        </div>
        <div className="w-8" />
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${
            open ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0 fixed lg:static z-40 top-14 lg:top-0 left-0 h-[calc(100vh-3.5rem)] lg:h-screen w-72 bg-slate-900 text-slate-100 transition-transform duration-200 ease-in-out flex flex-col`}
        >
          <div className="hidden lg:flex items-center gap-2 px-6 h-16 border-b border-slate-800">
            <Drumstick className="w-6 h-6 text-blue-400" />
            <div>
              <div className="font-bold tracking-tight">Cerebro Financiero</div>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
            {links.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`nav-${to.slice(1)}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-blue-600 text-white font-medium"
                      : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  }`
                }
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="p-4 border-t border-slate-800">
            <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">
              {user?.role === "admin" ? "Administrador" : "Captura"}
            </div>
            <div className="text-sm font-medium truncate" data-testid="user-email">{user?.email}</div>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 w-full bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Salir
            </Button>
          </div>
        </aside>

        {/* Backdrop */}
        {open && (
          <div
            className="lg:hidden fixed inset-0 top-14 bg-black/40 z-30"
            onClick={() => setOpen(false)}
          />
        )}

        {/* Main */}
        <main className="flex-1 min-h-screen lg:min-h-screen overflow-x-hidden">
          <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-7xl mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
}
